import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import * as d3 from 'd3';
import { RotateCcw, Eye, EyeOff, Maximize2, Minimize2, X, Filter, Info, FileText, Link2, Star } from 'lucide-react';
import { useData, KnowledgeGraphData } from '../context/DataContext';

interface Node extends d3.SimulationNodeDatum {
  id: string;
  name: string;
  type: string;
  size: number;
  full_title?: string;
  freq?: number;
  section_id?: number;
  color?: string;
}

interface Link extends d3.SimulationLinkDatum<Node> {
  source: string | Node;
  target: string | Node;
  relation?: string;
  weight?: number;
  color?: string;
}

interface KnowledgeGraphProps {
  externalViewMode?: 'micro' | 'macro' | 'files';
  onViewModeChange?: (mode: 'micro' | 'macro' | 'files') => void;
}

const KnowledgeGraph: React.FC<KnowledgeGraphProps> = ({
  externalViewMode,
  onViewModeChange
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const simulationRef = useRef<d3.Simulation<Node, Link> | null>(null);

  const {
    sections,
    selectedSectionIndex,
    setSelectedSectionIndex,
    microKGs,
    fetchSectionMacroKG,
    sectionMacroKGs,
    fetchMacroKG,
    macroKG,
    selectedDocIds,
    isLoading,
    error
  } = useData();

  const [isGraphVisible, setIsGraphVisible] = useState(true);
  const [internalViewMode, setInternalViewMode] = useState<'micro' | 'macro' | 'files'>('micro');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [highlightedType, setHighlightedType] = useState<string | null>(null);
  const [showLegend, setShowLegend] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filesKG, setFilesKG] = useState<KnowledgeGraphData | null>(null);
  const [selectedFileNode, setSelectedFileNode] = useState<any>(null);
  const [fileDetails, setFileDetails] = useState<any>(null);
  const [showFilePopup, setShowFilePopup] = useState(false);

  // Use external view mode if provided, otherwise use internal
  const viewMode = externalViewMode ?? internalViewMode;

  // Get current graph data based on view mode
  const currentGraphData = useMemo(() => {
    console.log('🔍 currentGraphData calculation:', {
      viewMode,
      selectedSectionIndex,
      sectionsLength: sections.length,
      microKGsKeys: microKGs ? Object.keys(microKGs) : null,
      sectionMacroKGsKeys: Object.keys(sectionMacroKGs),
      selectedDocIds,
      filesKG: filesKG ? { nodes: filesKG.nodes?.length, edges: filesKG.edges?.length } : null
    });
    
    if (viewMode === 'files') {
      // Use the files-based aggregated KG
      if (filesKG && filesKG.nodes && filesKG.nodes.length > 0) {
        console.log('✅ Using files KG with selected documents');
        return filesKG;
      }
      console.log('❌ No files KG available');
      return null;
    } else if (viewMode === 'micro') {
      const currentSection = sections[selectedSectionIndex];
      if (currentSection && microKGs && microKGs[currentSection.title]) {
        console.log('✅ Using micro KG for section:', currentSection.title);
        return microKGs[currentSection.title];
      }
      console.log('❌ No micro KG found for section:', currentSection?.title);
    } else {
      // For macro view, use section-specific macro KG if available
      if (sectionMacroKGs[selectedSectionIndex]) {
        console.log('✅ Using section macro KG for section:', selectedSectionIndex);
        return sectionMacroKGs[selectedSectionIndex];
      }
      console.log('❌ No section macro KG found for section:', selectedSectionIndex);
    }
    return null;
  }, [viewMode, selectedSectionIndex, sections, microKGs, sectionMacroKGs, filesKG, selectedDocIds]);
  
  // Fetch files KG when selected files change
  useEffect(() => {
    if (selectedDocIds.length > 0 && viewMode === 'files') {
      const loadFilesKG = async () => {
        const kg = await fetchMacroKG();
        if (kg) {
          setFilesKG(kg);
        }
      };
      loadFilesKG();
    }
  }, [selectedDocIds, viewMode, fetchMacroKG]);

  // Handle view mode change
  const handleViewModeChange = useCallback(async (mode: 'micro' | 'macro' | 'files') => {
    // Update internal state
    setInternalViewMode(mode);

    // Notify parent if callback provided
    onViewModeChange?.(mode);

    if (mode === 'macro' && !sectionMacroKGs[selectedSectionIndex]) {
      await fetchSectionMacroKG(selectedSectionIndex);
    } else if (mode === 'files' && selectedDocIds.length > 0) {
      const kg = await fetchMacroKG();
      if (kg) {
        setFilesKG(kg);
      }
    }
  }, [selectedSectionIndex, sectionMacroKGs, fetchSectionMacroKG, selectedDocIds, fetchMacroKG, onViewModeChange]);

  // Sync external view mode changes
  useEffect(() => {
    if (externalViewMode && externalViewMode !== internalViewMode) {
      handleViewModeChange(externalViewMode);
    }
  }, [externalViewMode]);

  // Fullscreen functions
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  const exitFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  // Highlight functions
  const handleTypeClick = useCallback((type: string) => {
    if (highlightedType === type) {
      setHighlightedType(null); // Clear highlight
    } else {
      setHighlightedType(type); // Highlight this type
    }
  }, [highlightedType]);

  const clearHighlight = useCallback(() => {
    setHighlightedType(null);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'F11') {
        event.preventDefault();
        toggleFullscreen();
      } else if (event.key === 'Escape' && isFullscreen) {
        exitFullscreen();
      } else if (event.key === 'Escape' && highlightedType) {
        clearHighlight();
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isFullscreen, toggleFullscreen, exitFullscreen, highlightedType, clearHighlight]);

  // Auto-fetch section macro KG when switching to macro mode
  useEffect(() => {
    if (viewMode === 'macro' && !sectionMacroKGs[selectedSectionIndex]) {
      fetchSectionMacroKG(selectedSectionIndex);
    }
  }, [viewMode, selectedSectionIndex, sectionMacroKGs, fetchSectionMacroKG]);

  // Main display types for the legend
  const displayTypes = useMemo(() => ({
    keyword: '#10B981',    // Green
    person: '#8B5CF6',     // Purple
    organization: '#3B82F6', // Blue
    location: '#F59E0B',   // Orange
    topic: '#EC4899',      // Pink
    key_point: '#EF4444',  // Red (changed from orange)
    concept: '#06B6D4'     // Cyan
  }), []);

  // Extended color map for all possible type variations
  const colorMap = useMemo(() => ({
    // Main types
    keyword: '#10B981',    // Green
    person: '#8B5CF6',     // Purple
    organization: '#3B82F6', // Blue
    location: '#F59E0B',   // Orange
    topic: '#EC4899',      // Pink
    key_point: '#EF4444',  // Red (changed from orange)
    concept: '#06B6D4',    // Cyan
    // Alternative type names that might be in the data
    keywords: '#10B981',   // Green
    people: '#8B5CF6',     // Purple
    organizations: '#3B82F6', // Blue
    locations: '#F59E0B',  // Orange
    topics: '#EC4899',    // Pink
    key_points: '#EF4444', // Red
    concepts: '#06B6D4',   // Cyan
    // Additional possible types
    entity: '#10B981',     // Green
    entities: '#10B981',   // Green
    term: '#10B981',       // Green
    terms: '#10B981',      // Green
    individual: '#8B5CF6', // Purple
    individuals: '#8B5CF6', // Purple
    company: '#3B82F6',    // Blue
    companies: '#3B82F6',  // Blue
    place: '#F59E0B',      // Orange
    places: '#F59E0B',     // Orange
    subject: '#EC4899',    // Pink
    subjects: '#EC4899',   // Pink
    highlight: '#EF4444',   // Red
    highlights: '#EF4444',  // Red
    idea: '#06B6D4',       // Cyan
    ideas: '#06B6D4'       // Cyan
  }), []);

  const colorDescriptions = useMemo(() => ({
    keyword: 'Keywords & Terms',
    person: 'People & Individuals',
    organization: 'Organizations & Companies',
    location: 'Places & Locations',
    topic: 'Topics & Subjects',
    key_point: 'Key Points & Highlights',
    concept: 'Concepts & Ideas',
    // Alternative type names
    keywords: 'Keywords & Terms',
    people: 'People & Individuals',
    organizations: 'Organizations & Companies',
    locations: 'Places & Locations',
    topics: 'Topics & Subjects',
    key_points: 'Key Points & Highlights',
    concepts: 'Concepts & Ideas',
    // Additional possible types
    entity: 'Keywords & Terms',
    entities: 'Keywords & Terms',
    term: 'Keywords & Terms',
    terms: 'Keywords & Terms',
    individual: 'People & Individuals',
    individuals: 'People & Individuals',
    company: 'Organizations & Companies',
    companies: 'Organizations & Companies',
    place: 'Places & Locations',
    places: 'Places & Locations',
    subject: 'Topics & Subjects',
    subjects: 'Topics & Subjects',
    highlight: 'Key Points & Highlights',
    highlights: 'Key Points & Highlights',
    idea: 'Concepts & Ideas',
    ideas: 'Concepts & Ideas'
  }), []);

  // Function to get the display type for a node type
  const getDisplayType = useCallback((nodeType: string) => {
    // Map actual node types to display categories
    if (['keyword', 'keywords', 'entity', 'entities', 'term', 'terms'].includes(nodeType)) {
      return 'keyword';
    }
    if (['person', 'people', 'individual', 'individuals'].includes(nodeType)) {
      return 'person';
    }
    if (['organization', 'organizations', 'company', 'companies'].includes(nodeType)) {
      return 'organization';
    }
    if (['location', 'locations', 'place', 'places'].includes(nodeType)) {
      return 'location';
    }
    if (['topic', 'topics', 'subject', 'subjects'].includes(nodeType)) {
      return 'topic';
    }
    if (['key_point', 'key_points', 'highlight', 'highlights'].includes(nodeType)) {
      return 'key_point';
    }
    if (['concept', 'concepts', 'idea', 'ideas'].includes(nodeType)) {
      return 'concept';
    }
    // Default fallback
    return 'keyword';
  }, []);

  const renderGraph = useCallback((data: KnowledgeGraphData) => {
    if (!svgRef.current || !data.nodes || data.nodes.length === 0) return;

    // Debug: Log node types to see what's actually in the data
    const nodeTypes = Array.from(new Set(data.nodes.map(node => node.type)));
    console.log('🔍 Node types in data:', nodeTypes);
    console.log('🎯 Highlighted type:', highlightedType);
    console.log('🎨 Color map keys:', Object.keys(colorMap));

    // Debug: Show mapping of actual types to display types
    const typeMapping = nodeTypes.map(type => ({
      actual: type,
      display: getDisplayType(type),
      color: colorMap[getDisplayType(type)] || '#666'
    }));
    console.log('🔄 Type mapping:', typeMapping);

    // Debug: Count nodes by display type
    const nodeCounts: Record<string, number> = {};
    data.nodes.forEach(node => {
      const displayType = getDisplayType(node.type);
      nodeCounts[displayType] = (nodeCounts[displayType] || 0) + 1;
    });
    console.log('📊 Node counts by display type:', nodeCounts);

    // Identify shared entities (entities that appear across multiple documents)
    // These are indicated by having connections to multiple document nodes
    const sharedEntities = new Set<string>();
    if (viewMode === 'files') {
      const documentNodes = data.nodes.filter((n: any) => n.type === 'document');
      const entityConnections = new Map<string, Set<string>>();

      data.edges.forEach((edge: any) => {
        const sourceNode = data.nodes.find(n => n.id === (typeof edge.source === 'string' ? edge.source : edge.source.id));
        const targetNode = data.nodes.find(n => n.id === (typeof edge.target === 'string' ? edge.target : edge.target.id));

        if (sourceNode && targetNode) {
          if (sourceNode.type === 'document' && targetNode.type !== 'document') {
            if (!entityConnections.has(targetNode.id)) {
              entityConnections.set(targetNode.id, new Set());
            }
            entityConnections.get(targetNode.id)!.add(sourceNode.id);
          } else if (targetNode.type === 'document' && sourceNode.type !== 'document') {
            if (!entityConnections.has(sourceNode.id)) {
              entityConnections.set(sourceNode.id, new Set());
            }
            entityConnections.get(sourceNode.id)!.add(targetNode.id);
          }
        }
      });

      // Mark entities connected to 2+ documents as shared
      entityConnections.forEach((docSet, entityId) => {
        if (docSet.size >= 2) {
          sharedEntities.add(entityId);
        }
      });

      console.log(`🔗 Found ${sharedEntities.size} shared entities across documents`);
    }

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Add SVG defs for glow effect on document nodes
    const defs = svg.append('defs');
    const filter = defs.append('filter')
      .attr('id', 'documentGlow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
    
    filter.append('feGaussianBlur')
      .attr('stdDeviation', '4')
      .attr('result', 'coloredBlur');
    
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;
    const centerX = width / 2;
    const centerY = height / 2;

    // Identify document nodes (should be at center)
    const documentNodes = data.nodes.filter((n: any) => n.type === 'document');
    const otherNodes = data.nodes.filter((n: any) => n.type !== 'document');
    
    console.log('📄 Document nodes found:', documentNodes.length);
    console.log('🔗 Other nodes:', otherNodes.length);

    // Position document nodes at center (arranged in a circle if multiple)
    // Only fix position if we have document nodes
    if (documentNodes.length > 0) {
      documentNodes.forEach((node: any, index: number) => {
        if (documentNodes.length === 1) {
          // Single document at exact center
          node.fx = centerX;
          node.fy = centerY;
        } else {
          // Multiple documents arranged in a circle around center
          const angle = (index * 2 * Math.PI) / documentNodes.length;
          const radius = Math.min(width, height) * 0.15;
          node.fx = centerX + radius * Math.cos(angle);
          node.fy = centerY + radius * Math.sin(angle);
        }
      });
    }

    // Create simulation with custom forces
    const simulation = d3.forceSimulation<Node, Link>(data.nodes)
      .force('link', d3.forceLink(data.edges)
        .id((d: any) => d.id)
        .distance((d: any) => {
          // Shorter links to document nodes
          const sourceIsDoc = typeof d.source === 'object' ? d.source.type === 'document' : 
            data.nodes.find((n: any) => n.id === d.source)?.type === 'document';
          const targetIsDoc = typeof d.target === 'object' ? d.target.type === 'document' : 
            data.nodes.find((n: any) => n.id === d.target)?.type === 'document';
          
          if (sourceIsDoc || targetIsDoc) {
            return 80; // Closer to documents
          }
          return 120; // Normal distance
        })
      )
      .force('charge', d3.forceManyBody()
        .strength((d: any) => {
          // Document nodes have stronger repulsion to push others away
          if (d.type === 'document') {
            return -800;
          }
          return -300;
        })
      )
      .force('center', d3.forceCenter(centerX, centerY).strength(0.1))
      .force('collision', d3.forceCollide()
        .radius((d: any) => {
          // Larger collision radius for document nodes
          if (d.type === 'document') {
            return (d.size || 50) + 20;
          }
          return (d.size || 10) + 5;
        })
      );
    
    // Add radial force to keep non-document nodes around documents (only if documents exist)
    if (documentNodes.length > 0) {
      simulation.force('radial', (alpha: number) => {
        otherNodes.forEach((node: any) => {
          // Calculate distance from nearest document
          let minDist = Infinity;
          let nearestDocX = centerX;
          let nearestDocY = centerY;
          
          documentNodes.forEach((docNode: any) => {
            const dx = (node.x || centerX) - (docNode.fx || centerX);
            const dy = (node.y || centerY) - (docNode.fy || centerY);
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < minDist) {
              minDist = dist;
              nearestDocX = docNode.fx || centerX;
              nearestDocY = docNode.fy || centerY;
            }
          });
          
          // Apply radial force to keep nodes at optimal distance from documents
          const dx = (node.x || centerX) - nearestDocX;
          const dy = (node.y || centerY) - nearestDocY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const targetDist = 150; // Optimal distance from documents
          
          if (dist > 0) {
            const force = (dist - targetDist) * alpha * 0.1;
            node.vx = (node.vx || 0) - (dx / dist) * force;
            node.vy = (node.vy || 0) - (dy / dist) * force;
          }
        });
      });
    }

    simulationRef.current = simulation;

    // Create link group with enhanced styling for PDF relationships
    const link = svg.append('g')
      .selectAll('line')
      .data(data.edges)
      .enter().append('line')
      .attr('stroke', (d: any) => {
        // Use relation-specific colors for PDF relationships
        if (d.relation_label) {
          return d.color || '#666';
        }
        return d.color || '#666';
      })
      .attr('stroke-opacity', (d: any) => {
        if (!highlightedType) {
          // PDF relationships get higher opacity
          if (d.relation === 'strongly_related' || d.relation === 'related' || d.relation === 'weakly_related' || d.relation === 'contains') {
            return 0.8;
          }
          return 0.6;
        }
        
        const sourceType = typeof d.source === 'string' ? 
          data.nodes.find(n => n.id === d.source)?.type : d.source.type;
        const targetType = typeof d.target === 'string' ? 
          data.nodes.find(n => n.id === d.target)?.type : d.target.type;
        
        const sourceDisplayType = getDisplayType(sourceType);
        const targetDisplayType = getDisplayType(targetType);
        
        return (sourceDisplayType === highlightedType || targetDisplayType === highlightedType) ? 0.8 : 0.1;
      })
      .attr('stroke-width', (d: any) => {
        // Thicker lines for PDF relationships
        if (d.relation === 'strongly_related') return Math.sqrt(d.weight || 1) * 3;
        if (d.relation === 'related' || d.relation === 'contains') return Math.sqrt(d.weight || 1) * 2.5;
        return Math.sqrt(d.weight || 1) * 2;
      })
      .attr('stroke-dasharray', (d: any) => {
        // Dashed lines for weak relationships
        if (d.relation === 'weakly_related') return '5,5';
        return '0';
      });
    
    // Add edge labels for PDF relationships (store reference for tick updates)
    const edgeLabelsGroup = svg.append('g').attr('class', 'edge-labels');
    const edgeLabels = edgeLabelsGroup
      .selectAll('text')
      .data(data.edges.filter((d: any) => d.relation_label && (d.relation === 'strongly_related' || d.relation === 'related')))
      .enter().append('text')
      .attr('class', 'edge-label')
      .text((d: any) => d.relation_label || '')
      .attr('font-size', '10px')
      .attr('font-family', 'Arial, sans-serif')
      .attr('fill', (d: any) => d.color || '#666')
      .attr('text-anchor', 'middle')
      .attr('pointer-events', 'none')
      .style('opacity', 0.8)
      .style('font-weight', 'bold');

    // Use already declared documentNodes and otherNodes from above
    
    // Create rectangles for document nodes
    const docNode = svg.append('g')
      .selectAll('rect')
      .data(documentNodes)
      .enter().append('rect')
      .attr('width', (d: any) => Math.max(d.size || 60, 60))
      .attr('height', (d: any) => Math.max(d.size || 60, 60))
      .attr('rx', 8) // Rounded corners
      .attr('ry', 8)
      .attr('x', (d: any) => -(d.size || 60) / 2)
      .attr('y', (d: any) => -(d.size || 60) / 2)
      .attr('fill', (d: any) => d.color || '#8B5CF6')
      .attr('stroke', '#fff')
      .attr('stroke-width', 4)
      .attr('opacity', 1)
      .style('cursor', 'pointer')
      .style('filter', 'url(#documentGlow)')
      .on('click', async (event, d: any) => {
        event.stopPropagation();
        console.log('📄 PDF node clicked:', d);
        setSelectedFileNode(d);
        
        // Ensure doc_id exists
        const docId = d.doc_id || d.id?.replace('file_', '');
        if (!docId) {
          console.error('❌ No doc_id found in node:', d);
          return;
        }
        
        // Fetch file details
        try {
          const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
            ? 'http://20.107.168.51:8002'
            : `${window.location.protocol}//${window.location.hostname}:8002`;
          console.log('🔍 Fetching file details for:', docId);
          const response = await fetch(`${API_BASE_URL}/file-details/${docId}?session_id=default`);
          if (response.ok) {
            const details = await response.json();
            console.log('✅ File details fetched:', details);
            setFileDetails(details);
            setShowFilePopup(true);
          } else {
            console.warn('⚠️ API failed, using node data');
            // Use node data if API fails
            setFileDetails({
              doc_id: docId,
              file_name: d.full_title || d.name,
              display_name: d.name,
              summary: d.summary || 'No summary available.',
              important_subjects: d.important_subjects || [],
              related_node_names: d.related_node_names || [],
              common_nodes: d.common_nodes || [],
              sections_count: d.sections_count || 0
            });
            setShowFilePopup(true);
          }
        } catch (err) {
          console.error('❌ Error fetching file details:', err);
          // Use node data if API fails
          setFileDetails({
            doc_id: docId,
            file_name: d.full_title || d.name,
            display_name: d.name,
            summary: d.summary || 'No summary available.',
            important_subjects: d.important_subjects || [],
            related_node_names: d.related_node_names || [],
            common_nodes: d.common_nodes || [],
            sections_count: d.sections_count || 0
          });
          setShowFilePopup(true);
        }
      });
    
    // Create circles for other nodes
    const node = svg.append('g')
      .selectAll('circle')
      .data(otherNodes)
      .enter().append('circle')
      .attr('r', (d: any) => d.size || 10)
      .attr('fill', (d: any) => {
        const nodeColor = d.color || (colorMap as any)[d.type] || '#666';
        if (!highlightedType) return nodeColor;
        const displayType = getDisplayType(d.type);
        return displayType === highlightedType ? nodeColor : '#444444';
      })
      .attr('stroke', (d: any) => {
        if (sharedEntities.has(d.id)) {
          return '#fbbf24'; // Gold color
        }
        if (!highlightedType) return '#fff';
        const displayType = getDisplayType(d.type);
        return displayType === highlightedType ? '#fff' : '#666';
      })
      .attr('stroke-width', (d: any) => {
        if (sharedEntities.has(d.id)) {
          return 3;
        }
        if (!highlightedType) return 2;
        const displayType = getDisplayType(d.type);
        return displayType === highlightedType ? 3 : 1;
      })
      .attr('opacity', (d: any) => {
        if (!highlightedType) return 1;
        const displayType = getDisplayType(d.type);
        return displayType === highlightedType ? 1 : 0.3;
      })
      .style('cursor', 'pointer')
      .call(d3.drag<SVGCircleElement, Node>()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended) as any);

    // Labels for document nodes
    const docLabel = svg.append('g')
      .selectAll('text')
      .data(documentNodes)
      .enter().append('text')
      .text((d: any) => d.name)
      .attr('font-size', '14px')
      .attr('font-family', 'Arial, sans-serif')
      .attr('font-weight', 'bold')
      .attr('fill', '#fff')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('opacity', 1)
      .style('pointer-events', 'none');
    
    // Labels for other nodes
    const label = svg.append('g')
      .selectAll('text')
      .data(otherNodes)
      .enter().append('text')
      .text((d: any) => d.name)
      .attr('font-size', '12px')
      .attr('font-family', 'Arial, sans-serif')
      .attr('font-weight', 'normal')
      .attr('fill', (d: any) => {
        if (!highlightedType) return '#fff';
        const displayType = getDisplayType(d.type);
        return displayType === highlightedType ? '#fff' : '#666';
      })
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('opacity', (d: any) => {
        if (!highlightedType) return 1;
        const displayType = getDisplayType(d.type);
        return displayType === highlightedType ? 1 : 0.3;
      })
      .style('pointer-events', 'none');

    node.append('title')
      .text((d: any) => {
        let text = `${d.name}\nType: ${d.type}`;
        if (d.type === 'document') {
          text += `\n📄 PDF Document`;
          if (d.sections_count) text += `\nSections: ${d.sections_count}`;
          if (d.metadata?.color_label) text += `\n${d.metadata.color_label}`;
        }
        if (d.freq) text += `\nFrequency: ${d.freq}`;
        if (sharedEntities.has(d.id)) text += `\n⭐ SHARED ENTITY - Appears in multiple documents`;
        return text;
      });
    
    // Add tooltips for edges showing relationship details
    link.append('title')
      .text((d: any) => {
        let text = `Relation: ${d.relation_label || d.relation || 'Unknown'}`;
        if (d.similarity) text += `\nSimilarity: ${(d.similarity * 100).toFixed(1)}%`;
        if (d.shared_items_preview) text += `\nShared: ${d.shared_items_preview}`;
        if (d.shared_keywords && d.shared_keywords.length > 0) {
          text += `\nKeywords: ${d.shared_keywords.slice(0, 5).join(', ')}`;
        }
        if (d.shared_entities && d.shared_entities.length > 0) {
          text += `\nEntities: ${d.shared_entities.slice(0, 3).join(', ')}`;
        }
        return text;
      });

    simulation.on('tick', () => {
      link
        .attr('x1', (d: any) => {
          const source = typeof d.source === 'object' ? d.source : data.nodes.find(n => n.id === d.source);
          return source?.x || 0;
        })
        .attr('y1', (d: any) => {
          const source = typeof d.source === 'object' ? d.source : data.nodes.find(n => n.id === d.source);
          return source?.y || 0;
        })
        .attr('x2', (d: any) => {
          const target = typeof d.target === 'object' ? d.target : data.nodes.find(n => n.id === d.target);
          return target?.x || 0;
        })
        .attr('y2', (d: any) => {
          const target = typeof d.target === 'object' ? d.target : data.nodes.find(n => n.id === d.target);
          return target?.y || 0;
        });

      docNode
        .attr('x', (d: any) => (d.x || 0) - (d.size || 60) / 2)
        .attr('y', (d: any) => (d.y || 0) - (d.size || 60) / 2);

      docLabel
        .attr('x', (d: any) => d.x || 0)
        .attr('y', (d: any) => d.y || 0);

      node
        .attr('cx', (d: any) => d.x)
        .attr('cy', (d: any) => d.y);

      label
        .attr('x', (d: any) => d.x)
        .attr('y', (d: any) => d.y);
      
      // Update edge label positions
      edgeLabels
        .attr('x', (d: any) => {
          const source = typeof d.source === 'object' ? d.source : data.nodes.find(n => n.id === d.source);
          const target = typeof d.target === 'object' ? d.target : data.nodes.find(n => n.id === d.target);
          return ((source?.x || 0) + (target?.x || 0)) / 2;
        })
        .attr('y', (d: any) => {
          const source = typeof d.source === 'object' ? d.source : data.nodes.find(n => n.id === d.source);
          const target = typeof d.target === 'object' ? d.target : data.nodes.find(n => n.id === d.target);
          return ((source?.y || 0) + (target?.y || 0)) / 2;
        });
    });

    function dragstarted(event: any, d: any) {
      // Don't allow dragging document nodes
      if (d.type === 'document') return;
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event: any, d: any) {
      // Don't allow dragging document nodes
      if (d.type === 'document') return;
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event: any, d: any) {
      // Don't allow dragging document nodes
      if (d.type === 'document') return;
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

  }, [colorMap, highlightedType, getDisplayType]);

  // Re-render when data or highlight changes
  useEffect(() => {
    if (currentGraphData && isGraphVisible) {
      renderGraph(currentGraphData);
    }
  }, [currentGraphData, isGraphVisible, renderGraph]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (currentGraphData && isGraphVisible) {
        renderGraph(currentGraphData);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentGraphData, isGraphVisible, renderGraph]);

  const resetGraph = useCallback(() => {
    if (simulationRef.current && svgRef.current) {
      const width = svgRef.current.clientWidth;
      const height = svgRef.current.clientHeight;
      const centerX = width / 2;
      const centerY = height / 2;
      
      // Re-center document nodes
      const nodes = simulationRef.current.nodes() as Node[];
      const documentNodes = nodes.filter((n: any) => n.type === 'document');
      
      documentNodes.forEach((node: any, index: number) => {
        if (documentNodes.length === 1) {
          node.fx = centerX;
          node.fy = centerY;
        } else {
          const angle = (index * 2 * Math.PI) / documentNodes.length;
          const radius = Math.min(width, height) * 0.15;
          node.fx = centerX + radius * Math.cos(angle);
          node.fy = centerY + radius * Math.sin(angle);
        }
      });
      
      simulationRef.current.alpha(1).restart();
    }
  }, []);

  const handleRefresh = useCallback(() => {
    if (viewMode === 'macro' && !sectionMacroKGs[selectedSectionIndex]) {
      fetchSectionMacroKG(selectedSectionIndex);
    }
    if (currentGraphData && isGraphVisible) {
      renderGraph(currentGraphData);
    }
  }, [viewMode, selectedSectionIndex, sectionMacroKGs, fetchSectionMacroKG, currentGraphData, isGraphVisible, renderGraph]);

  // Color Legend Component with click functionality
  const ColorLegend = () => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 border border-gray-600/50"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white">Entity Types</h3>
        <button
          onClick={() => setShowLegend(!showLegend)}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <X size={16} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        {Object.entries(displayTypes).map(([type, color]) => (
          <div
            key={type}
            className={`flex items-center space-x-2 cursor-pointer p-2 rounded-lg transition-all duration-200 ${
              highlightedType === type
                ? 'bg-purple-600/30 border border-purple-400'
                : 'hover:bg-gray-700/50'
            }`}
            onClick={() => handleTypeClick(type)}
          >
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: color }}
            />
            <span className={`text-sm ${
              highlightedType === type ? 'text-purple-300 font-semibold' : 'text-gray-300'
            }`}>
              {colorDescriptions[type as keyof typeof colorDescriptions]}
            </span>
          </div>
        ))}
      </div>
      {viewMode === 'files' && (
        <>
          <div className="mt-3 pt-3 border-t border-gray-600">
            <h4 className="text-xs font-semibold text-gray-400 mb-2 uppercase">PDF Relationships</h4>
            <div className="space-y-2">
              <div className="flex items-center space-x-2 p-1.5 rounded">
                <div className="w-4 h-0.5 bg-purple-500" />
                <span className="text-xs text-gray-300">Strongly Related</span>
              </div>
              <div className="flex items-center space-x-2 p-1.5 rounded">
                <div className="w-4 h-0.5 bg-indigo-500" />
                <span className="text-xs text-gray-300">Related</span>
              </div>
              <div className="flex items-center space-x-2 p-1.5 rounded">
                <div className="w-4 h-0.5 bg-indigo-300 border-dashed border-t" style={{borderTopWidth: '2px'}} />
                <span className="text-xs text-gray-300">Weakly Related</span>
              </div>
              <div className="flex items-center space-x-2 p-1.5 rounded">
                <div className="w-4 h-0.5 bg-green-500" />
                <span className="text-xs text-gray-300">Contains Section</span>
              </div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-600">
            <div className="flex items-center space-x-2 p-2 bg-yellow-500/10 rounded-lg">
              <div className="w-3 h-3 rounded-full bg-gray-600 border-2 border-yellow-500" />
              <span className="text-sm text-yellow-300">
                ⭐ Shared Entity (Multiple Files)
              </span>
            </div>
          </div>
        </>
      )}
      {highlightedType && (
        <div className="mt-3 pt-3 border-t border-gray-600">
          <button
            onClick={clearHighlight}
            className="w-full text-xs text-purple-400 hover:text-purple-300 transition-colors"
          >
            Clear Highlight (Esc)
          </button>
        </div>
      )}
    </motion.div>
  );

  // Color Filter Component
  const ColorFilter = () => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 border border-gray-600/50"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white">Highlight by Type</h3>
        <div className="flex space-x-2">
          <button
            onClick={clearHighlight}
            className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
          >
            Clear All
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {Object.entries(displayTypes).map(([type, color]) => (
          <div 
            key={type} 
            className={`flex items-center space-x-2 cursor-pointer p-2 rounded-lg transition-all duration-200 ${
              highlightedType === type 
                ? 'bg-purple-600/30 border border-purple-400' 
                : 'hover:bg-gray-700/50'
            }`}
            onClick={() => handleTypeClick(type)}
          >
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: color }}
            />
            <span className={`text-sm ${
              highlightedType === type ? 'text-purple-300 font-semibold' : 'text-gray-300'
            }`}>
              {colorDescriptions[type as keyof typeof colorDescriptions]}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  );

  if (isFullscreen) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-50 bg-slate-900 flex flex-col"
      >
        {/* Fullscreen Header */}
        <div className="flex items-center justify-between p-4 bg-slate-800/90 border-b border-white/20">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-white">Knowledge Graph - Fullscreen</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleViewModeChange('micro')}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 ${
                  viewMode === 'micro'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                Micro KG
              </button>
              <button
                onClick={() => handleViewModeChange('macro')}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 ${
                  viewMode === 'macro'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                Macro KG
              </button>
              {selectedDocIds.length > 0 && (
                <button
                  onClick={() => handleViewModeChange('files')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 ${
                    viewMode === 'files'
                      ? 'bg-purple-600 text-white'
                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                >
                  Files KG
                </button>
              )}
            </div>
            {(viewMode === 'micro' || viewMode === 'macro') && sections.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-white/70">Section:</span>
                <select
                  value={selectedSectionIndex}
                  onChange={(e) => setSelectedSectionIndex(Number(e.target.value))}
                  className="px-3 py-1 rounded-lg bg-white/10 text-white border border-white/20 text-sm min-w-[200px]"
                >
                  {sections.map((section, index) => (
                    <option key={index} value={index} className="bg-slate-800 text-white">
                      {section.title}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {viewMode === 'files' && selectedDocIds.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-white/70">Selected Files:</span>
                <span className="text-sm text-purple-300 font-semibold">
                  {selectedDocIds.length} file{selectedDocIds.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}
            {highlightedType && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-white/70">Highlighted:</span>
                <span className="text-sm text-purple-300 font-semibold">
                  {colorDescriptions[highlightedType as keyof typeof colorDescriptions]}
                </span>
                <button
                  onClick={clearHighlight}
                  className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowLegend(!showLegend)}
              className="p-2 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 transition-colors"
              title="Toggle Legend"
            >
              <Info className="h-5 w-5 text-gray-300" />
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="p-2 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 transition-colors"
              title="Toggle Filters"
            >
              <Filter className="h-5 w-5 text-gray-300" />
            </button>
            <button
              onClick={() => setIsGraphVisible(!isGraphVisible)}
              className="p-2 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 transition-colors"
            >
              {isGraphVisible ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
            <button
              onClick={handleRefresh}
              className="p-2 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 transition-colors"
            >
              <RotateCcw size={18} />
            </button>
            <button
              onClick={exitFullscreen}
              className="p-2 rounded-lg bg-red-600/20 text-red-400 hover:bg-red-600/30 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        
        {/* Fullscreen Graph Container */}
        <div className="flex-1 relative">
          <div
            ref={containerRef}
            className="w-full h-full bg-gradient-to-br from-slate-800/50 to-purple-900/30"
          >
            <svg ref={svgRef} className="w-full h-full" />
          </div>
          
          {/* Fullscreen Controls */}
          <div className="absolute top-4 right-4 flex flex-col space-y-2">
            {showLegend && <ColorLegend />}
            {showFilters && <ColorFilter />}
          </div>
          
          {/* Fullscreen Footer */}
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between bg-slate-800/90 rounded-lg px-4 py-2 border border-white/20">
            <div className="flex items-center space-x-4 text-sm text-white/80">
              {currentGraphData && currentGraphData.nodes && (
                <>
                  <span className="font-medium">Nodes:</span> {currentGraphData.nodes.length} |
                  <span className="font-medium ml-2">Edges:</span> {currentGraphData.edges.length}
                </>
              )}
            </div>
            <div className="text-sm text-white/80">
              <span className="font-medium">View:</span> {
                viewMode === 'files' ? 'Files (Selected Documents)' :
                viewMode === 'macro' ? 'Macro (Section Summary)' : 
                'Micro (Section Detail)'
              }
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 p-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-700/50 p-8"
        >
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
              Knowledge Graph
            </h1>
            
            <div className="flex items-center space-x-4">
              {/* View Mode Toggle */}
              <div className="flex bg-gray-700/50 rounded-lg p-1">
                <button
                  onClick={() => handleViewModeChange('micro')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    viewMode === 'micro'
                      ? 'bg-purple-600 text-white shadow-lg'
                      : 'text-gray-300 hover:text-white hover:bg-gray-600/50'
                  }`}
                >
                  Micro KG
                </button>
                <button
                  onClick={() => handleViewModeChange('macro')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    viewMode === 'macro'
                      ? 'bg-purple-600 text-white shadow-lg'
                      : 'text-gray-300 hover:text-white hover:bg-gray-600/50'
                  }`}
                >
                  Macro KG
                </button>
                {selectedDocIds.length > 0 && (
                  <button
                    onClick={() => handleViewModeChange('files')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      viewMode === 'files'
                        ? 'bg-purple-600 text-white shadow-lg'
                        : 'text-gray-300 hover:text-white hover:bg-gray-600/50'
                    }`}
                  >
                    Files KG
                  </button>
                )}
              </div>

              {/* Section Selector (only for micro and macro views) */}
              {(viewMode === 'micro' || viewMode === 'macro') && sections.length > 0 && (
                <select
                  value={selectedSectionIndex}
                  onChange={(e) => setSelectedSectionIndex(Number(e.target.value))}
                  className="bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {sections.map((section, index) => (
                    <option key={index} value={index}>
                      {section.title}
                    </option>
                  ))}
                </select>
              )}

              {/* Highlighted Type Display */}
              {highlightedType && (
                <div className="flex items-center space-x-2 bg-purple-600/20 border border-purple-400/30 rounded-lg px-3 py-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: colorMap[highlightedType as keyof typeof colorMap] }}
                  />
                  <span className="text-sm text-purple-300 font-semibold">
                    {colorDescriptions[highlightedType as keyof typeof colorDescriptions]}
                  </span>
                  <button
                    onClick={clearHighlight}
                    className="text-xs text-purple-400 hover:text-purple-300 transition-colors ml-2"
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* Control Buttons */}
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowLegend(!showLegend)}
                  className="p-2 bg-gray-700/50 hover:bg-gray-600/50 rounded-lg transition-colors duration-200"
                  title="Toggle Legend"
                >
                  <Info className="h-5 w-5 text-gray-300" />
                </button>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="p-2 bg-gray-700/50 hover:bg-gray-600/50 rounded-lg transition-colors duration-200"
                  title="Toggle Filters"
                >
                  <Filter className="h-5 w-5 text-gray-300" />
                </button>
                <button
                  onClick={toggleFullscreen}
                  className="p-2 bg-gray-700/50 hover:bg-gray-600/50 rounded-lg transition-colors duration-200 group relative"
                  title="Toggle Fullscreen (F11)"
                >
                  {isFullscreen ? <Minimize2 className="h-5 w-5 text-gray-300" /> : <Maximize2 className="h-5 w-5 text-gray-300" />}
                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    {isFullscreen ? 'Exit Fullscreen (Esc)' : 'Fullscreen (F11)'}
                  </span>
                </button>
                <button
                  onClick={resetGraph}
                  className="p-2 bg-gray-700/50 hover:bg-gray-600/50 rounded-lg transition-colors duration-200"
                  title="Reset Graph"
                >
                  <RotateCcw className="h-5 w-5 text-gray-300" />
                </button>
                <button
                  onClick={() => setIsGraphVisible(!isGraphVisible)}
                  className="p-2 bg-gray-700/50 hover:bg-gray-600/50 rounded-lg transition-colors duration-200"
                  title={isGraphVisible ? "Hide Graph" : "Show Graph"}
                >
                  {isGraphVisible ? (
                    <EyeOff className="h-5 w-5 text-gray-300" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-300" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Controls Panel */}
          <div className="flex space-x-4 mb-6">
            {showLegend && <ColorLegend />}
            {showFilters && <ColorFilter />}
          </div>

          {/* Graph Container */}
          <div 
            ref={containerRef}
            className="relative w-full h-[600px] bg-gradient-to-br from-slate-800/50 to-purple-900/30 rounded-xl border border-white/20 overflow-hidden"
          >
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
                  <p className="text-gray-300">Loading knowledge graph...</p>
                </div>
              </div>
            ) : !currentGraphData || !currentGraphData.nodes || currentGraphData.nodes.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <p className="text-gray-300 text-lg">
                    {error ? `Error: ${error}` : 'No knowledge graph data available'}
                  </p>
                </div>
              </div>
            ) : (
              <svg
                ref={svgRef}
                className="w-full h-full"
                style={{ background: 'transparent' }}
              />
            )}
          </div>

          {/* Graph Info */}
          {currentGraphData && currentGraphData.nodes && (
            <div className="mt-4 flex items-center justify-between text-sm text-gray-300">
              <div>
                <span className="font-medium">Nodes:</span> {currentGraphData.nodes.length} | 
                <span className="font-medium ml-2">Edges:</span> {currentGraphData.edges.length}
                {highlightedType && (
                  <span className="ml-4 text-purple-300">
                    | Highlighted: {colorDescriptions[highlightedType as keyof typeof colorDescriptions]}
                  </span>
                )}
              </div>
              <div>
                <span className="font-medium">View:</span> {
                  viewMode === 'files' ? 'Files (Selected Documents)' :
                  viewMode === 'macro' ? 'Macro (Section Summary)' : 
                  'Micro (Section Detail)'
                }
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* File Details Popup */}
      {showFilePopup && fileDetails && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setShowFilePopup(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-gray-800 via-purple-900/20 to-gray-800 rounded-2xl shadow-2xl border border-purple-500/30 max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="text-white" size={24} />
                <div>
                  <h2 className="text-2xl font-bold text-white">{fileDetails.display_name || fileDetails.file_name}</h2>
                  <p className="text-purple-100 text-sm">{fileDetails.sections_count || 0} sections</p>
                </div>
              </div>
              <button
                onClick={() => setShowFilePopup(false)}
                className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Summary */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                  <Info size={20} />
                  Summary
                </h3>
                <p className="text-gray-300 leading-relaxed bg-gray-800/50 p-4 rounded-lg border border-gray-700">
                  {fileDetails.summary || 'No summary available.'}
                </p>
              </div>

              {/* Important Subjects */}
              {fileDetails.important_subjects && fileDetails.important_subjects.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <Star size={20} className="text-yellow-400" />
                    Most Important Subjects
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {fileDetails.important_subjects.map((subject: string, idx: number) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 bg-gradient-to-r from-purple-600/30 to-blue-600/30 text-white rounded-lg text-sm border border-purple-500/30"
                      >
                        {subject}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Related Nodes */}
              {fileDetails.related_node_names && fileDetails.related_node_names.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <Link2 size={20} className="text-blue-400" />
                    Related Nodes ({fileDetails.related_node_names.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {fileDetails.related_node_names.slice(0, 15).map((nodeName: string, idx: number) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 bg-blue-600/20 text-blue-200 rounded-lg text-sm border border-blue-500/30"
                      >
                        {nodeName}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Common Nodes */}
              {fileDetails.common_nodes && fileDetails.common_nodes.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <Star size={20} className="text-orange-400" />
                    Common Nodes with Other Files ({fileDetails.common_nodes.length})
                  </h3>
                  <div className="space-y-2">
                    {fileDetails.common_nodes.slice(0, 10).map((common: any, idx: number) => (
                      <div
                        key={idx}
                        className="bg-orange-600/10 border border-orange-500/30 rounded-lg p-3 flex items-center justify-between"
                      >
                        <span className="text-orange-200 font-medium">{common.content}</span>
                        <span className="text-gray-400 text-sm">Shared with: {common.shared_with_name || common.shared_with}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default KnowledgeGraph;